import * as React from "react";
import { css } from "@emotion/core";
import { createPortal } from "react-dom";
import * as mapboxgl from "mapbox-gl";
import * as turf from "@turf/helpers";
import bbox from "@turf/bbox";
import { Button, ButtonGroup, ResizeSensor } from "@blueprintjs/core";
import { getCoords } from "utils/";
import { isValidCoordinates } from "utils/mapping/coords";

import { Spacer } from "components/common";

interface IMarkerCollection {
  [key: number]: mapboxgl.Marker;
}

(mapboxgl as any).accessToken = process.env.REACT_APP_MAPBOX_KEY;

const centerMapToAssets = (map, images) => {
  const all = bbox(
    turf.featureCollection(images.map((image) => turf.point(getCoords(image))))
  );

  map.fitBounds(
    [
      [all[0], all[1]],
      [all[2], all[3]],
    ],
    { animate: false, padding: 64 }
  );
};

const Marker = ({ image, map, markerSelection, markerDeletion }) => {
  const complete = image.uploaded;
  const missing = image.uploaded === false && image.handle === undefined;

  const selected = markerSelection.isSelected(image.uid);
  const markedForDeletion = markerDeletion ? markerDeletion[image.id] : false;

  const [marker, setMarker] = React.useState(null);

  React.useEffect(() => {
    const markerElement = document.createElement("div");

    const marker = new mapboxgl.Marker(markerElement)
      .setLngLat(getCoords(image))
      .addTo(map.current);

    setMarker(marker);
  }, [image]);

  if (marker === null) return null;

  return createPortal(
    <div
      style={{ position: "relative" }}
      onClick={() => markerSelection.select(image.uid)}
    >
      <svg width="16px" height="16px" viewBox="0 0 16 16">
        {markedForDeletion && (
          <circle fill="#C23030" cx="8" cy="8" r="8"></circle>
        )}
        <circle
          fill={
            selected
              ? "#e6e6e6"
              : complete
                ? "#0D8050"
                : missing
                  ? "#C23030"
                  : "#BF8C0A"
          }
          cx="8"
          cy="8"
          r="6"
        ></circle>
        <circle
          fill={complete ? "#0F9960" : missing ? "#DB3737" : "#D99E0B"}
          cx="8"
          cy="8"
          r="4"
        ></circle>
      </svg>
    </div>,
    marker.getElement()
  );
};

const MapPreview = ({
  survey,
  images,
  center,
  setCenter,
  zoom,
  setZoom,
  markerSelection,
  markerDeletion,
  organizationLongitude,
  organizationLatitude,
}) => {
  const map = React.useRef(null);
  const [markers, setMarkers] = React.useState<IMarkerCollection>({});

  React.useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: "preview-map",
        style: "mapbox://styles/crawfo10/cj1seiwo9000t2rpfqf1g2yuc",
        center,
        zoom,
      });

      map.current.on("load", function () {
        const center = map.current.getCenter();
        setCenter([center.lng.toFixed(5), center.lat.toFixed(5)]);
      });

      map.current.on("moveend", function () {
        const center = map.current.getCenter();
        setCenter([center.lng.toFixed(5), center.lat.toFixed(5)]);
      });

      map.current.on("zoomend", function () {
        setZoom(map.current.getZoom());
      });
    }
  }, [center, zoom, setCenter, setZoom]);

  React.useEffect(() => {
    if (map.current) {
      const { lng, lat } = map.current.getCenter();
      const current = [lng.toFixed(5).toString(), lat.toFixed(5).toString()];

      if (images.length > 0) {
        return;
      }

      if (center[0] !== current[0] || center[1] !== current[1]) {
        map.current.setCenter(
          isValidCoordinates(center[1], center[0])
            ? center
            : [organizationLongitude, organizationLatitude]
        );
      }
    }
  }, [center, images.length, organizationLongitude, organizationLatitude]);

  React.useEffect(() => {
    if (map.current && images.length > 0) {
      centerMapToAssets(map.current, images);
    }
  }, [images.length]);

  return (
    <ResizeSensor onResize={() => map.current && map.current.resize()}>
      <div
        css={css`
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
        `}
      >
        {images.length > 0 && (
          <>
            <ButtonGroup fill>
              <Button
                text="Set Location to Images Center"
                disabled={images.length === 0}
                onClick={() => {
                  centerMapToAssets(map.current, images);
                }}
              />
            </ButtonGroup>
            <Spacer />
          </>
        )}
        <div id="preview-map" style={{ width: "100%", height: "100%" }} />
        {map.current &&
          images.map((image) => {
            return (
              <Marker
                key={image.uid}
                image={image}
                map={map}
                markerSelection={markerSelection}
                markerDeletion={markerDeletion}
              />
            );
          })}
      </div>
    </ResizeSensor>
  );
};

export default class Boundary extends React.Component {
  state = {
    error: "",
  };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {}

  render() {
    if (this.state.error) return <div>{this.state.error.message}</div>;
    return <MapPreview {...this.props} />;
  }
}
